<template>
	<v-dialog
		v-model="localShowDialog"
		:width="$vuetify.display.mobile ? '95%' : '500px'"
		:persistent="deleteInProgress"
		@click:outside="handleDismiss"
		@keydown.esc="handleDismiss"
	>
		<v-card>
			<v-card-title class="dialog-header sgnl-subtitle-medium">
				Delete {{ deletePathData.type == "dir" ? "Directory" : "File" }}?
				<v-spacer />
				<v-btn
					icon
					variant="text"
					size="small"
					@click="handleDismiss"
				>
					<Close16 />
				</v-btn>
			</v-card-title>
			<v-divider />
			<v-card-text class="dialog-body">
				<div class="delete-prompt-group">
					<p class="sgnl-paragraph-s-regular">
						Are you sure you want to delete the following
						{{ deletePathData.type == "dir" ? "directory" : "file" }}?
					</p>

					<p class="sgnl-code-default">
						{{ deletePathData.path }}
					</p>
				</div>
				<v-alert
					:icon="false"
					class="sgnl-paragraph-s-regular"
					type="warning"
					variant="outlined"
					elevation="0"
					border="start"
				>
					<strong>
						This operation can not be undone.
						<div v-if="dirNotEmpty()">
							Directory is not empty and has {{ deletePathData.children?.length ?? 0 }} item{{
								(deletePathData.children?.length ?? 0) > 1 ? "s" : ""
							}}.
						</div>
					</strong>
				</v-alert>

				<template v-if="dirNotEmpty()">
					<div class="delete-confirm-group">
						<p class="sgnl-paragraph-s-regular">
							Type "permanently delete" to confirm deletion of the directory and all its contents.
						</p>
						<v-text-field
							placeholder="permanently delete"
							v-model="recursiveOptionConfirmationText"
						/>
					</div>
				</template>
			</v-card-text>
			<v-divider />
			<v-card-actions class="dialog-footer">
				<v-btn
					variant="outlined"
					@click="handleDismiss"
					>Cancel</v-btn
				>
				<v-btn
					variant="flat"
					color="error"
					:loading="deleteInProgress"
					:disabled="deleteButtonDisabled()"
					@click="commitDeletePath"
				>
					Delete
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import Close16 from "@carbon/icons-vue/es/close/16";
import { sdk } from "@/js/sdk";
import { FileTree } from "@/types/general";
import { monitoringCaptureError } from "@/plugins/monitoring";

type ComponentData = { deleteInProgress: boolean; recursiveOptionConfirmationText: string };

export default defineComponent({
	name: "SigCloudStorageFileDeleteDialog",
	components: { Close16 },
	props: {
		showDialog: { type: Boolean, default: false },
		deletePathData: { type: Object as PropType<FileTree>, required: true },
	},
	emits: {
		"update:showDialog": (_data: boolean) => true,
		"close-dialog": (_data: boolean) => true,
		"delete-successful": (itemType: string) => typeof itemType === "string",
	},
	data: (): ComponentData => ({
		deleteInProgress: false,
		recursiveOptionConfirmationText: "",
	}),
	computed: {
		localShowDialog: {
			get() {
				return this.showDialog;
			},
			set(value) {
				this.$emit("update:showDialog", value);
			},
		},
	},
	methods: {
		async commitDeletePath() {
			this.deleteInProgress = true;
			const isRecursiveAvailable = this.recursiveOptionConfirmationText == "permanently delete";
			try {
				await sdk.files.delete(this.deletePathData.path, {
					directory: this.deletePathData.type == "dir",
					recursive: isRecursiveAvailable,
				});

				this.$emit("delete-successful", this.deletePathData.type === "dir" ? "directory" : "file");
				this.closeDialog();
			} catch (error) {
				monitoringCaptureError(error, "Delete file from user cloud storage");
			} finally {
				this.deleteInProgress = false;
			}
		},
		handleDismiss() {
			if (this.deleteInProgress) {
				return;
			}
			this.closeDialog();
		},
		closeDialog() {
			this.$emit("close-dialog", false);
		},
		dirNotEmpty() {
			return this.deletePathData.type == "dir" && (this.$props.deletePathData.children?.length ?? 0) != 0;
		},
		deleteButtonDisabled() {
			const dirNotEmpty = this.deletePathData.type == "dir" && (this.deletePathData.children?.length ?? 0) != 0;
			const isRecursiveAvailable = this.recursiveOptionConfirmationText == "permanently delete";
			return dirNotEmpty && !isRecursiveAvailable;
		},
	},
});
</script>

<style scoped>
.delete-prompt-group {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-4);
}

.delete-confirm-group {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-4);
}
</style>
